import React, { useState, useCallback, useMemo } from 'react';
import { Stack, Box, IconButton, Tooltip, Typography } from '@mui/material';
import Moment from 'moment/moment';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Company, CompanyApplication, CompanyDocumentListItem, Contact, Service } from '../../../../interfaces/users.interface';
import StandardDialog from '../../../Modals/StandardDialog';
import CompanyDocumentUploadForm from './CompanyDocumentUploadForm';
import ChipsWithOverflow from '../../../Layout/ChipsWithOverflow';
import { DataCell, DataRow, DataRowActions } from '../../../Tables/DataList';
import { tightTableSx } from '../../../Tables/dataTableStyles';
import { deleteCompanyDocument } from '../../../../services/companyDocuments';
import { getCompanyApps, getCompanyDocuments, getCompanyServices, getCompanySites } from '../../../../services/companyDetail';
import { useCompanyResource } from '../../../Vendors/useCompanyResource';
import { TabEmpty, TabSkeletonRows, DEFAULT_PER_PAGE } from '../../../Vendors/companyTabUi';
import { useSectionPager } from '../../../Vendors/sectionPager';
const ALL = 9999;

type CompanyDocumentsTabProps = {
  company: Company;
  companyCallback: any;
  userCanAdminCompany: boolean;
  uploadOpen?: boolean;
  onUploadClose?: () => void;
  onDocumentsChange?: () => void;
};

export default function CompanyDocumentsTab(props: CompanyDocumentsTabProps) {
  const { company, companyCallback, userCanAdminCompany, uploadOpen: externalUploadOpen, onUploadClose, onDocumentsChange } = props;
  const [internalUploadOpen, setInternalUploadOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);

  const { items: documents, count, loading, refetch } = useCompanyResource<CompanyDocumentListItem>(
    getCompanyDocuments,
    company.id,
    page,
    pageSize,
  );
  useSectionPager({ count, page, pageSize, setPage, setPageSize });

  const uploadOpen = externalUploadOpen !== undefined ? externalUploadOpen : internalUploadOpen;

  // The upload form's selectors need these collections, but the rows read each
  // document's own names — so only fetch them (unpaged) once the dialog is open.
  const { items: apps } = useCompanyResource<CompanyApplication>(getCompanyApps, company.id, 1, ALL, uploadOpen);
  const { items: sites } = useCompanyResource<Contact>(getCompanySites, company.id, 1, ALL, uploadOpen);
  const { items: services } = useCompanyResource<Service>(getCompanyServices, company.id, 1, ALL, uploadOpen);

  // Company-shaped object the upload form reads from.
  const formCompany = useMemo<Company>(
    () => ({ ...company, companyApplications: apps, locations: sites, services }),
    [company, apps, sites, services],
  );

  const handleUploadClose = useCallback(() => {
    if (onUploadClose) {
      onUploadClose();
    } else {
      setInternalUploadOpen(false);
    }
  }, [onUploadClose]);

  // Refresh this page and let the shell refresh its tab count.
  const handleDocumentsChange = useCallback(() => {
    refetch();
    if (onDocumentsChange) onDocumentsChange();
  }, [refetch, onDocumentsChange]);

  const handleUploaded = useCallback(
    (updatedCompany: Company) => {
      companyCallback(updatedCompany);
      handleUploadClose();
      handleDocumentsChange();
    },
    [companyCallback, handleUploadClose, handleDocumentsChange],
  );

  const handleDeleteDocument = useCallback(
    (doc: CompanyDocumentListItem) => {
      deleteCompanyDocument(doc).then(() => handleDocumentsChange());
    },
    [handleDocumentsChange],
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <StandardDialog title='Upload Document' handleClose={handleUploadClose} isOpen={uploadOpen}>
        <CompanyDocumentUploadForm company={formCompany} uploadCallback={handleUploaded} />
      </StandardDialog>

      <Box sx={tightTableSx}>
        <DataRow header>
          <DataCell xs={3}>Document</DataCell>
          <DataCell xs={2}>Sites</DataCell>
          <DataCell xs={2}>Services</DataCell>
          <DataCell xs={2}>Applications</DataCell>
          <DataCell xs={2}>Upload Date</DataCell>
          <DataCell xs={1}>&nbsp;</DataCell>
        </DataRow>

        {loading && <TabSkeletonRows rows={4} columns={[3, 2, 2, 2, 2, 1]} />}

        {!loading && documents.length === 0 && <TabEmpty label='No documents' />}

        {!loading && documents.map((doc) => (
          <DataRow key={doc.id} onClick={() => {}} hover>
            <DataCell xs={3}>
              <Typography sx={{ fontSize: '1.2em' }}>
                <a href={doc.document} target='_blank' rel='noreferrer'>{doc.name}</a>
              </Typography>
            </DataCell>
            <DataCell xs={2}>{ChipsWithOverflow(doc.sites?.map((s) => s.name)?.filter(Boolean))}</DataCell>
            <DataCell xs={2}>{ChipsWithOverflow(doc.services?.map((s) => s.name)?.filter(Boolean))}</DataCell>
            <DataCell xs={2}>{ChipsWithOverflow(doc.applicationVersions?.map((a) => a.name)?.filter(Boolean))}</DataCell>
            <DataCell xs={2}>{Moment(doc.createdAt).utc().format('MM/DD/YYYY')}</DataCell>
            <DataCell xs={1}>
              <DataRowActions>
                <a href={doc.document} target="_blank" rel="noreferrer">
                  <Tooltip title="View Document">
                    <IconButton size='small'>
                      <LinkOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </a>
                {userCanAdminCompany && (
                  <Tooltip title="Delete Document">
                    <IconButton size='small' onClick={() => handleDeleteDocument(doc)} disabled={!userCanAdminCompany}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </DataRowActions>
            </DataCell>
          </DataRow>
        ))}
      </Box>

    </Stack>
  );
}
